// Ida y vuelta entre una regla y su editor: en pantalla pesos, porcentajes y factores; en el motor
// centavos y puntos base.
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionBonusModeEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionBonusModeEnum';
import { CommissionCountByEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionCountByEnum';
import { CommissionMetricEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionMetricEnum';
import { CommissionOperatorEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionOperatorEnum';
import { CommissionPayTimingEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionPayTimingEnum';
import type { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import type { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';

type RuleInput = RetailCommission.CommissionRuleInput;
type RuleFields = Pick<RetailCommission.CommissionRule, keyof RuleInput>;

const { FIXED, PERCENT } = CommissionBonusModeEnum;
const { POSICION_VENTA_EN_PERIODO, COLOCACION_PERIODO, NPI_M30_VENDEDOR, SKUS_DISTINTOS_PERIODO } =
    CommissionMetricEnum;
const { BETWEEN, GTE, IN, LT } = CommissionOperatorEnum;
const { ON_SALE, ON_CUT } = CommissionPayTimingEnum;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Lo que se teclea en el editor. Los números van como texto para poder dejarlos vacíos. */
export interface RuleFormValues {
    name: string;
    description: string;
    countBy: CommissionCountByEnum;
    saleBonusMode: CommissionBonusModeEnum;
    saleBonus: string;
    thresholdBonus: string;
    multiplier: string;
    exclusive: boolean;
    couponBonusMode: CommissionBonusModeEnum;
    couponBonus: string;
    payTiming: CommissionPayTimingEnum;
    /** Porcentaje (1 a 99) que se paga al registrar la venta; vacío, sin adelanto. */
    advancePct: string;
    metric: CommissionMetricEnum | '';
    operator: CommissionOperatorEnum;
    conditionValue: string;
    conditionValueTo: string;
    minSample: string;
    tipos: ProductCategoryEnum[];
    gamas: ProductTierEnum[];
    skus: string[];
    tiendas: string[];
    effectiveFrom: string;
    effectiveTo: string;
    permanent: boolean;
    priority: number;
}

export type RuleFormErrors = Partial<Record<keyof RuleFormValues | 'levers', string>>;

/** `YYYY-MM-DD` de un día que existe: el motor rechaza `2026-02-30`. */
export function isRealDate(value: string): boolean {
    if (!ISO_DATE.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function emptyRuleForm(today: string): RuleFormValues {
    return {
        name: '',
        description: '',
        countBy: CommissionCountByEnum.PIECE,
        saleBonusMode: FIXED,
        saleBonus: '',
        thresholdBonus: '',
        multiplier: '',
        exclusive: false,
        couponBonusMode: PERCENT,
        couponBonus: '',
        payTiming: ON_CUT,
        advancePct: '',
        metric: '',
        operator: GTE,
        conditionValue: '',
        conditionValueTo: '',
        minSample: '',
        tipos: [],
        gamas: [],
        skus: [],
        tiendas: [],
        effectiveFrom: today,
        effectiveTo: '',
        permanent: true,
        priority: 0,
    };
}

/** La condición con que arranca cada métrica, como en el mock. */
export function conditionDefaults(
    metric: CommissionMetricEnum | '',
): Pick<
    RuleFormValues,
    'metric' | 'operator' | 'conditionValue' | 'conditionValueTo' | 'minSample'
> {
    if (metric === POSICION_VENTA_EN_PERIODO) {
        return {
            metric,
            operator: BETWEEN,
            conditionValue: '1',
            conditionValueTo: '5',
            minSample: '',
        };
    }
    if (metric === NPI_M30_VENDEDOR)
        return { metric, operator: LT, conditionValue: '5', conditionValueTo: '', minSample: '5' };
    return {
        metric,
        operator: GTE,
        conditionValue: metric === '' ? '' : '0',
        conditionValueTo: '',
        minSample: '',
    };
}

/** Centavos y puntos base valen cien por unidad en pantalla: 8000 → "80", 250 → "2.5". */
const hundredths = (value: number) => String(value / 100);
const toHundredths = (text: string) => Math.round(Number(text) * 100);
const factorText = (bps: number) => String(bps / 10_000);

/** El monto se teclea en pesos y el NPI en %; las ventas y los modelos, en enteros. */
const inHundredths = (metric: CommissionMetricEnum) =>
    metric === COLOCACION_PERIODO || metric === NPI_M30_VENDEDOR;

export function ruleToForm(rule: RuleFields): RuleFormValues {
    const { bonoPorVenta, bonoUmbral, multiplicador, bonoPorCuponPagado } = rule.levers;
    const condition = rule.condition;
    const values = condition
        ? Array.isArray(condition.value)
            ? condition.value
            : [condition.value]
        : [];
    const text = (value: number) =>
        condition && inHundredths(condition.metric) ? hundredths(value) : String(value);
    return {
        name: rule.name,
        description: rule.description ?? '',
        countBy: rule.countBy ?? CommissionCountByEnum.PIECE,
        saleBonusMode: bonoPorVenta?.mode ?? FIXED,
        saleBonus: bonoPorVenta ? hundredths(bonoPorVenta.value) : '',
        thresholdBonus: bonoUmbral ? hundredths(bonoUmbral.amountCents) : '',
        multiplier: multiplicador ? factorText(multiplicador.factorBps) : '',
        exclusive: rule.replacesOthers,
        couponBonusMode: bonoPorCuponPagado?.mode ?? PERCENT,
        couponBonus: bonoPorCuponPagado ? hundredths(bonoPorCuponPagado.value) : '',
        payTiming: rule.payTiming ?? ON_CUT,
        advancePct: rule.onSaleAdvanceBps != null ? String(rule.onSaleAdvanceBps / 100) : '',
        metric: condition?.metric ?? '',
        operator: condition?.operator ?? GTE,
        conditionValue:
            condition?.operator === IN
                ? values.map(text).join(', ')
                : values.length > 0
                  ? text(values[0])
                  : '',
        conditionValueTo:
            condition?.operator === BETWEEN && values.length > 1 ? text(values[1]) : '',
        minSample: condition?.minSample != null ? String(condition.minSample) : '',
        tipos: [...rule.scope.tipos],
        gamas: [...rule.scope.gamas],
        skus: [...rule.scope.skus],
        tiendas: [...rule.scope.tiendas],
        effectiveFrom: rule.effectiveFrom,
        effectiveTo: rule.effectiveTo ?? '',
        permanent: rule.effectiveTo === null,
        priority: rule.priority,
    };
}

/** `null` si el campo está vacío; si no, el número (o `NaN`). */
function parsed(text: string): number | null {
    return text.trim() === '' ? null : Number(text);
}

export const DECIMALS = 'Usa hasta dos decimales.';
/** Pesos y porcentajes viajan en centavos y puntos base: más de dos decimales se perderían. */
export const twoDecimals = (value: number) =>
    Math.abs(value * 100 - Math.round(value * 100)) < 1e-6;

/** Un multiplicador vacío o ×1 no cambia nada; con él, "exclusiva" no aplica. */
export function multiplierApplies(text: string): boolean {
    const value = parsed(text);
    return value !== null && Number.isFinite(value) && value > 0 && value !== 1;
}

/** "Al momento" solo cabe en una regla que únicamente paga bono por venta. */
export function onSaleAllowed(
    v: Pick<RuleFormValues, 'saleBonus' | 'thresholdBonus' | 'multiplier' | 'couponBonus'>,
): boolean {
    const filled = (text: string) => text.trim() !== '';
    return (
        filled(v.saleBonus) &&
        !filled(v.thresholdBonus) &&
        !filled(v.multiplier) &&
        !filled(v.couponBonus)
    );
}

/** El adelanto cabe en bono por venta que se paga al cierre: "al momento" ya paga todo al vender. */
export function advanceAllowed(
    v: Pick<
        RuleFormValues,
        'saleBonus' | 'thresholdBonus' | 'multiplier' | 'couponBonus' | 'payTiming'
    >,
): boolean {
    return onSaleAllowed(v) && v.payTiming !== ON_SALE;
}

export const ADVANCE_RANGE = 'Escribe un porcentaje entero de 1 a 99.';

export const ON_SALE_ONLY = 'Al momento solo aplica a reglas que únicamente pagan bono por venta.';

/**
 * Qué cuenta "Número de ventas" según las palancas: en el bono por venta, la posición de cada venta
 * en el alcance; en umbral, multiplicador y renta, todo lo del periodo al cerrar el corte.
 */
export function positionHelpOf(
    v: Pick<
        RuleFormValues,
        'countBy' | 'saleBonus' | 'thresholdBonus' | 'multiplier' | 'couponBonus'
    >,
): string {
    const [one, many] =
        v.countBy === CommissionCountByEnum.SALE ? ['venta', 'ventas'] : ['pieza', 'piezas'];
    const perSale = parsed(v.saleBonus) !== null;
    const atCut =
        parsed(v.thresholdBonus) !== null ||
        multiplierApplies(v.multiplier) ||
        parsed(v.couponBonus) !== null;
    if (atCut && !perSale) {
        return `Cuenta todas las ${many} del vendedor en el periodo, sin importar el alcance, y se revisa al cerrar el corte: "≥ 15" paga cuando llega a 15.`;
    }
    if (atCut) {
        return `En el bono por venta cuenta la posición de cada ${one} en el alcance ("entre 1 y 5" paga las primeras cinco). En el umbral, el multiplicador y la renta cuenta todas las ${many} del periodo, sin importar el alcance, al cerrar el corte.`;
    }
    return v.countBy === CommissionCountByEnum.SALE
        ? 'Cuenta las ventas del vendedor en el periodo con al menos una pieza en el alcance de esta regla. "Entre 1 y 5" paga las primeras cinco.'
        : 'Cuenta las piezas del vendedor en el periodo que caen en el alcance de esta regla. "Entre 1 y 5" paga las primeras cinco.';
}

export function validateRuleForm(v: RuleFormValues): RuleFormErrors {
    const errors: RuleFormErrors = {};
    if (v.name.trim() === '') errors.name = 'La regla necesita un nombre.';

    // `scale`: cuánto vale una unidad de pantalla en el motor (centavos o puntos base).
    const lever = (
        key: 'saleBonus' | 'thresholdBonus' | 'multiplier' | 'couponBonus',
        isPercent: boolean,
        scale: number,
    ): boolean => {
        const value = parsed(v[key]);
        if (value === null) return false;
        if (!Number.isFinite(value) || value <= 0) errors[key] = 'Debe ser mayor que 0.';
        else if (scale === 100 && !twoDecimals(value)) errors[key] = DECIMALS;
        else if (Math.round(value * scale) <= 0) errors[key] = 'Debe ser mayor que 0.';
        else if (isPercent && value > 100) errors[key] = 'El porcentaje no pasa de 100.';
        return true;
    };
    const filled = [
        lever('saleBonus', v.saleBonusMode === PERCENT, 100),
        lever('thresholdBonus', false, 100),
        lever('multiplier', false, 10_000),
        lever('couponBonus', v.couponBonusMode === PERCENT, 100),
    ];
    if (!filled.some(Boolean)) errors.levers = 'Llena al menos una palanca.';
    if (v.payTiming === ON_SALE && !onSaleAllowed(v)) errors.payTiming = ON_SALE_ONLY;
    if (advanceAllowed(v) && v.advancePct.trim() !== '') {
        const pct = Number(v.advancePct);
        if (!Number.isInteger(pct) || pct < 1 || pct > 99) errors.advancePct = ADVANCE_RANGE;
    }

    if (v.metric !== '') {
        const integer =
            v.metric === POSICION_VENTA_EN_PERIODO || v.metric === SKUS_DISTINTOS_PERIODO;
        const message = integer ? 'Pon un número entero desde 0.' : 'Pon un número desde 0.';
        const cents = inHundredths(v.metric);
        const problem = (text: string): string | undefined => {
            const value = parsed(text);
            if (value === null || !Number.isFinite(value) || value < 0) return message;
            if (integer && !Number.isInteger(value)) return message;
            return cents && !twoDecimals(value) ? DECIMALS : undefined;
        };
        const items = v.conditionValue
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== '');
        const valueProblem =
            v.operator !== IN
                ? problem(v.conditionValue)
                : items.length === 0
                  ? message
                  : items.map(problem).find((found) => found !== undefined);
        if (valueProblem !== undefined) errors.conditionValue = valueProblem;
        if (v.operator === BETWEEN) {
            const toProblem = problem(v.conditionValueTo);
            if (toProblem !== undefined) errors.conditionValueTo = toProblem;
            else if (
                errors.conditionValue === undefined &&
                Number(v.conditionValueTo) < Number(v.conditionValue)
            ) {
                errors.conditionValueTo = 'El final no puede ser menor que el inicio.';
            }
        }
        if (v.metric === NPI_M30_VENDEDOR && v.minSample.trim() !== '') {
            const sample = Number(v.minSample);
            if (!Number.isInteger(sample) || sample < 1)
                errors.minSample = 'Pon un número entero desde 1.';
        }
    }

    if (!isRealDate(v.effectiveFrom)) errors.effectiveFrom = 'Elige desde cuándo aplica.';
    if (!v.permanent) {
        if (!isRealDate(v.effectiveTo)) errors.effectiveTo = 'Elige hasta cuándo aplica.';
        else if (v.effectiveTo < v.effectiveFrom)
            errors.effectiveTo = 'No puede terminar antes de empezar.';
    }
    return errors;
}

function conditionOf(v: RuleFormValues): RetailCommission.CommissionRuleCondition | null {
    const metric = v.metric;
    if (metric === '') return null;
    const toNumber = (text: string) => (inHundredths(metric) ? toHundredths(text) : Number(text));
    const value =
        v.operator === BETWEEN
            ? [toNumber(v.conditionValue), toNumber(v.conditionValueTo)]
            : v.operator === IN
              ? v.conditionValue
                    .split(',')
                    .map((item) => item.trim())
                    .filter((item) => item !== '')
                    .map(toNumber)
              : toNumber(v.conditionValue);
    const minSample =
        metric === NPI_M30_VENDEDOR && v.minSample.trim() !== '' ? Number(v.minSample) : null;
    return { metric, operator: v.operator, value, minSample };
}

/** Lo que se manda al motor. Supone un formulario que pasó `validateRuleForm`. */
export function formToInput(v: RuleFormValues): RuleInput {
    const filled = (text: string) => text.trim() !== '';
    const multiplicador = filled(v.multiplier)
        ? { factorBps: Math.round(Number(v.multiplier) * 10_000) }
        : null;
    return {
        name: v.name.trim(),
        description: v.description.trim() === '' ? null : v.description.trim(),
        countBy: v.countBy,
        levers: {
            bonoPorVenta: filled(v.saleBonus)
                ? { mode: v.saleBonusMode, value: toHundredths(v.saleBonus) }
                : null,
            bonoUmbral: filled(v.thresholdBonus)
                ? { amountCents: toHundredths(v.thresholdBonus) }
                : null,
            multiplicador,
            bonoPorCuponPagado: filled(v.couponBonus)
                ? { mode: v.couponBonusMode, value: toHundredths(v.couponBonus) }
                : null,
        },
        scope: {
            tipos: [...v.tipos],
            gamas: [...v.gamas],
            skus: [...v.skus],
            tiendas: [...v.tiendas],
        },
        condition: conditionOf(v),
        payTiming: v.payTiming,
        onSaleAdvanceBps:
            advanceAllowed(v) && v.advancePct.trim() !== '' ? Number(v.advancePct) * 100 : null,
        effectiveFrom: v.effectiveFrom,
        effectiveTo: v.permanent ? null : v.effectiveTo,
        priority: v.priority,
        // "Exclusiva" solo tiene sentido con un multiplicador que cambie algo.
        replacesOthers: multiplierApplies(v.multiplier) && v.exclusive,
    };
}
